/* eslint-disable @typescript-eslint/no-empty-function */
import { getEnabledElement } from '@cornerstonejs/core';
import type { Types } from '@cornerstonejs/core';

import {
  drawHandles as drawHandlesSvg,
  drawTextBox as drawTextBoxSvg,
} from '../../drawingSvg';
import { getViewportIdsWithToolToRender } from '../../utilities/viewportFilters';
import { hideElementCursor } from '../../cursors/elementCursor';
import { Events } from '../../enums';
import type {
  Annotation,
  EventTypes,
  PublicToolProps,
  SVGDrawingHelper,
  ToolProps,
} from '../../types';

import triggerAnnotationRenderForViewportIds from '../../utilities/triggerAnnotationRenderForViewportIds';
import getViewportICamera from '../../utilities/getViewportICamera';
import ProbeTool from './ProbeTool';
import type { ProbeAnnotation } from '../../types/ToolSpecificAnnotationTypes';
import type { StyleSpecifier } from '../../types/AnnotationStyle';
import { utilities as cornerstoneUtilities } from '@cornerstonejs/core';

const cs3dLogger = cornerstoneUtilities.logger.toolsLog.getLogger(
  'tools.annotation.DragProbeTool'
);

class DragProbeTool extends ProbeTool {
  static toolName = 'DragProbe';

  editData: {
    annotation: Annotation;
    viewportIdsToRender: string[];
    newAnnotation?: boolean;
  } | null;
  eventDispatchDetail: {
    viewportId: string;
    renderingEngineId: string;
  };
  isDrawing: boolean;
  isHandleOutsideImage: boolean;

  constructor(
    toolProps: PublicToolProps = {},
    defaultToolProps: ToolProps = {
      supportedInteractionTypes: ['Mouse', 'Touch'],
      configuration: {
        shadow: true,
        preventHandleOutsideImage: false,
        getTextLines: defaultGetTextLines,
      },
    }
  ) {
    super(toolProps, defaultToolProps);
  }

  postMouseDownCallback = (
    evt: EventTypes.InteractionEventType
  ): ProbeAnnotation => {
    const eventDetail = evt.detail;
    const { currentPoints, element } = eventDetail;

    const enabledElement = getEnabledElement(element);
    const { viewport } = enabledElement;

    // Reached with the TOUCH_START event via postTouchStartCallback (both the
    // explicit alias below and the dispatcher-level fallback).
    const isTouch = evt.type === Events.TOUCH_START;
    const worldPos = this.getTouchAdjustedWorldPos(
      viewport,
      currentPoints,
      isTouch
    );

    this.isDrawing = true;
    // Native ("next") viewports expose no getCamera; read orientation via the bridge.
    const camera = getViewportICamera(viewport);
    const { viewPlaneNormal, viewUp } = camera;

    const referencedImageId = this.getReferencedImageId(
      viewport,
      worldPos,
      viewPlaneNormal,
      viewUp
    );

    const annotation: ProbeAnnotation = {
      invalidated: true,
      highlighted: true,
      isVisible: true,
      metadata: {
        toolName: this.getToolName(),
        viewPlaneNormal: <Types.Point3>[...viewPlaneNormal],
        viewUp: <Types.Point3>[...viewUp],
        FrameOfReferenceUID: viewport.getFrameOfReferenceUID(),
        referencedImageId,
      },
      data: {
        label: '',
        handles: { points: [<Types.Point3>[...worldPos]] },
        cachedStats: {},
      },
    };

    const viewportIdsToRender = getViewportIdsWithToolToRender(
      element,
      this.getToolName()
    );

    this.editData = {
      annotation,
      newAnnotation: true,
      viewportIdsToRender,
    };
    this._activateModify(element);

    hideElementCursor(element);

    evt.preventDefault();

    triggerAnnotationRenderForViewportIds(viewportIdsToRender);

    return annotation;
  };

  postTouchStartCallback = (
    evt: EventTypes.InteractionEventType
  ): ProbeAnnotation => {
    return this.postMouseDownCallback(evt);
  };

  renderAnnotation = (
    enabledElement: Types.IEnabledElement,
    svgDrawingHelper: SVGDrawingHelper
  ): boolean => {
    let renderStatus = false;
    const { viewport } = enabledElement;

    if (!this.editData) {
      return renderStatus;
    }

    const annotations = this.filterInteractableAnnotationsForElement(
      viewport.element,
      [this.editData.annotation]
    );

    if (!annotations?.length) {
      return renderStatus;
    }

    const renderingEngine = viewport.getRenderingEngine();

    const styleSpecifier: StyleSpecifier = {
      toolGroupId: this.toolGroupId,
      toolName: this.getToolName(),
      viewportId: enabledElement.viewport.id,
    };

    const annotation = this.editData.annotation as ProbeAnnotation;
    const annotationUID = annotation.annotationUID;
    const data = annotation.data;
    const point = data.handles.points[0];
    const canvasCoordinates = viewport.worldToCanvas(point);

    const targetId = this.getTargetId(viewport, data);
    styleSpecifier.annotationUID = annotationUID;

    const { color } = this.getAnnotationStyle({
      annotation,
      styleSpecifier,
    });

    if (
      !data.cachedStats[targetId] ||
      (data.cachedStats[targetId] as Record<string, unknown>).value === null
    ) {
      data.cachedStats[targetId] = {
        Modality: null,
        index: null,
        value: null,
      };

      this._calculateCachedStats(annotation, renderingEngine, enabledElement);
    } else if (annotation.invalidated) {
      this._calculateCachedStats(annotation, renderingEngine, enabledElement);
    }

    // If rendering engine has been destroyed while rendering
    if (!viewport.getRenderingEngine()) {
      cs3dLogger.warn('Rendering Engine has been destroyed');
      return renderStatus;
    }

    const handleGroupUID = '0';

    drawHandlesSvg(
      svgDrawingHelper,
      annotationUID,
      handleGroupUID,
      [canvasCoordinates],
      { color }
    );

    renderStatus = true;

    const textLines = this.configuration.getTextLines(data, targetId);
    if (textLines) {
      const textCanvasCoordinates = [
        canvasCoordinates[0] + 6,
        canvasCoordinates[1] - 6,
      ];

      const textUID = '0';
      drawTextBoxSvg(
        svgDrawingHelper,
        annotationUID,
        textUID,
        textLines,
        [textCanvasCoordinates[0], textCanvasCoordinates[1]],
        this.getLinkedTextBoxStyle(styleSpecifier, annotation)
      );
    }

    return renderStatus;
  };
}

function defaultGetTextLines(data, targetId): string[] {
  const cachedVolumeStats = data.cachedStats[targetId];
  const { index, value, modalityUnit } = cachedVolumeStats;

  if (value === undefined) {
    return;
  }

  const textLines = [];

  textLines.push(`(${index[0]}, ${index[1]}, ${index[2]})`);

  textLines.push(`${value.toFixed(2)} ${modalityUnit}`);

  return textLines;
}

export default DragProbeTool;
